import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import WhiteNavbar from "components/Navbars/WhiteNavbar";
import Footer from "components/Footers/Footer";
import lawyers from "../assets/avukatlar/Avukatlar.json";

const LawyerDetail = () => {
  const { id } = useParams();
  const lawyer = (lawyers || []).find((l) => l.url === id);

  const parseList = (value) =>
    !value
      ? []
      : Array.isArray(value)
        ? value
        : String(value)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

  const parseArticles = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    return String(value)
      .split(/,(?=[^)]*(?:\(|$))/) // parantez dışındaki virgüllere göre böl
      .map((s) => s.trim())
      .filter(Boolean)
      .map((item) => {
        const match = item.match(/^(.*?)\s*\((.*?)\)$/);
        if (match) {
          return { title: match[1].trim(), note: match[2].trim() };
        }
        return { title: item, note: null };
      });
  };

  const parseEducation = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    const parts = String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    let result = [];
    for (let i = 0; i < parts.length; i++) {
      const text = parts[i];
      const year = /^\d{4}$/.test(parts[i + 1]) ? parts[i + 1] : null;
      if (year) {
        result.push({ school: text, year });
        i++; // yılı atla
      } else {
        result.push({ school: text, year: null }); // null → "-" koyma
      }
    }
    return result;
  };

  const educationItems = useMemo(
    () => parseEducation(lawyer?.education),
    [lawyer],
  );
  const articlesItems = useMemo(
    () => parseArticles(lawyer?.articles),
    [lawyer],
  );
  const practiceAreaItems = useMemo(
    () => parseList(lawyer?.practice_area),
    [lawyer],
  );
  const languageItems = useMemo(() => parseList(lawyer?.language), [lawyer]);

  if (!lawyer) {
    return (
      <div className="flex items-center justify-center w-full min-h-[60vh] font-sans">
        <div className="text-center">
          <p className="text-lg font-medium">Avukat bulunamadı.</p>
          <Link
            to="/"
            className="mt-3 inline-block text-blue-600 hover:underline"
          >
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  const email =
    (lawyer?.email && lawyer.email.trim() !== "" && lawyer.email) || "-";
  const phone =
    lawyer?.phone || lawyer?.phoneMobile || lawyer?.phoneOffice || "-";

  const canonical = `https://www.eksiogluhukukofisi.com/ekibimiz/${id}`;
  const pageTitle = `${lawyer?.name} | EKŞİOĞLU HUKUK`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`${lawyer?.name} - ${lawyer?.title} | Ekşioğlu Hukuk Ofisi uzman kadrosu ve çalışma alanları.`}
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:description" content="EKŞİOĞLU HUKUK" />
        <meta property="og:image" content={lawyer?.img} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <WhiteNavbar navbarSelection={"about-us"} />

      {/* HERO */}
      <section
        className="relative mt-[55px] w-full font-sans overflow-hidden"
        style={{
          backgroundImage: `url('/eksioglu-ofis.jpeg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 backdrop-blur-[6px] z-0" />

        <div className="relative z-10 mx-auto max-w-[1400px] px-5 pt-8 lg:pt-10 grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
          {/* Foto */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start self-end ">
            <img
              src={lawyer.pngImg}
              alt={lawyer.name}
              className="w-[90%] md:w-[75%] lg:w-[520px] h-auto object-contain max-h-[280px] sm:max-h-[320px] md:max-h-[380px] lg:max-h-[460px] shrink-0"
            />
          </div>
          {/* İsim / Title */}
          <div className="lg:col-span-7 xl:flex hidden flex-col justify-end self-end pb-2">
            <h1
              className="text-[32px] sm:text-[30px] md:lg-text-[35px] lg:text-[40px] xl:text-[45px] 2xl:text-[52px] font-extrabold leading-tight text-slate-100"
              style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.4)" }}
            >
              {lawyer?.name}
            </h1>
            <div className="mt-2 w-20 h-[3px] bg-[#DEA057]/70 rounded" />
            <p
              className="mt-3 text-lg sm:text-xl lg:text-2xl text-slate-100 font-semibold"
              style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.3)" }}
            >
              {lawyer?.title || "Avukat"}
            </p>
          </div>
        </div>
      </section>

      {/* Mobil Ekranlarda Name/Title */}
      <div className="w-full bg-[#F3F4F6] font-sans xl:hidden flex flex-col justify-center items-center">
        <h1
          className="text-[32px] sm:text-[42px] lg:text-[52px] font-extrabold leading-tight text-slate-800 text-center mt-2"
          style={{ textShadow: "1px 1px 4px rgba(0,0,0,0.4)" }}
        >
          {lawyer?.name}
        </h1>
        <div className="mt-2 w-20 h-[3px] bg-[#DEA057]/70 rounded" />
        <p
          className="mt-2 mb-4 text-lg sm:text-xl lg:text-2xl text-slate-700 font-semibold"
          style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.3)" }}
        >
          {lawyer?.title || "Avukat"}
        </p>
      </div>

      {/* İLETİŞİM BAR */}
      <section className="w-full bg-[#F3F4F6] xl:border-y border-b border-slate-200 font-sans">
        <div className="mx-auto max-w-[1400px] px-5 pt-3 grid grid-cols-1 md:grid-cols-3 xl:gap-[160px] gap-x-6 gap-y-4 items-center">
          <div className=" md:pl-5 lg:pl-8">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              E-posta
            </div>
            <div className="mt-1 text-[15px] break-all">{email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Telefon
            </div>
            <div className="mt-1 text-[15px]">{phone}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              İş Telefonu
            </div>
            <div className="mt-1 text-[15px]">(0216) 949 03 53</div>
          </div>
          <div className="lg:ml-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Sosyal Medya
            </div>
            <div className="mt-2 flex items-center gap-5">
              <a
                href={
                  lawyer.linkedin && lawyer.linkedin !== ""
                    ? lawyer.linkedin
                    : "https://www.linkedin.com/company/ek%C5%9Fio%C4%9Flu-hukuk-dan%C4%B1%C5%9Fmanl%C4%B1k/?viewAsMember=true"
                }
                target="_blank"
                rel="noreferrer"
                className="text-2xl text-blue-600 hover:text-blue-800 transition"
              >
                <i className="fab fa-linkedin" />
              </a>
              <a
                href="https://www.instagram.com/eksiogluhukukvedanismanlik/"
                target="_blank"
                rel="noreferrer"
                className="text-2xl text-pink-600 hover:text-pink-800 transition"
              >
                <i className="fab fa-instagram" />
              </a>
              <a
                href="https://x.com/eksioglu_hukuk"
                target="_blank"
                rel="noreferrer"
                className="text-2xl text-black hover:text-gray-700 transition"
              >
                <i className="fa-brands fa-x-twitter" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* İÇERİK */}
      <section className="w-full font-sans">
        <div className="mx-auto max-w-[1400px] px-5 py-10 flex flex-col gap-10">
          {/* 2 Sütun: Sol (Çalışma Alanları + Yayınlar) | Sağ (Eğitim + Dil) */}
          <div className="w-full flex flex-col lg:flex-row gap-10">
            {/* Sol Sütun */}
            <div className="w-full lg:w-1/2 space-y-10">
              <div>
                <h2 className="text-[20px] sm:text-[22px] font-bold">
                  Çalışma Alanları
                </h2>
                <div className="mt-4 space-y-2">
                  {practiceAreaItems.length ? (
                    practiceAreaItems.map((p, i) => (
                      <div key={i} className="leading-relaxed text-[15px]">
                        • {p}
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-500">-</div>
                  )}
                </div>
              </div>

              {articlesItems?.length > 0 && (
                <div>
                  <h2 className="text-[20px] sm:text-[22px] font-bold">
                    Yayınlar / Üyelikler
                  </h2>
                  <div className="mt-4 space-y-4">
                    {articlesItems.length === 1 && lawyer?.link ? (
                      // Tek madde varsa linkli göster
                      <a
                        className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black"
                        href={lawyer.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        • {articlesItems[0].title}{" "}
                        {articlesItems[0].note && (
                          <span className="italic text-[14px]">
                            ({articlesItems[0].note})
                          </span>
                        )}
                      </a>
                    ) : articlesItems.length === 2 &&
                      lawyer?.link &&
                      lawyer?.link2 ? (
                      <>
                        {/*  Her iki madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[0].title}{" "}
                          {articlesItems[0].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[0].note})
                            </span>
                          )}
                        </a>

                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link2}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[1].title}{" "}
                          {articlesItems[1].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[1].note})
                            </span>
                          )}
                        </a>
                      </>
                    ) : articlesItems.length === 2 && lawyer?.link ? (
                      <>
                        {/* İlk madde normal, ikinci linkli */}
                        <div className="leading-relaxed text-[15px] mb-2">
                          • {articlesItems[0].title}{" "}
                          {articlesItems[0].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[0].note})
                            </span>
                          )}
                        </div>

                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black"
                          href={lawyer.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[1].title}{" "}
                          {articlesItems[1].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[1].note})
                            </span>
                          )}
                        </a>
                      </>
                    ) : articlesItems.length === 3 && lawyer?.link2 ? (
                      <>
                        {/* İlk madde normal div */}
                        <div className="leading-relaxed text-[15px] block">
                          • {articlesItems[0].title}{" "}
                          {articlesItems[0].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[0].note})
                            </span>
                          )}
                        </div>

                        {/* İkinci madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[1].title}{" "}
                          {articlesItems[1].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[1].note})
                            </span>
                          )}
                        </a>

                        {/* Üçüncü madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link2}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[2].title}{" "}
                          {articlesItems[2].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[2].note})
                            </span>
                          )}
                        </a>
                      </>
                    ) : articlesItems.length === 4 && lawyer?.link3 ? (
                      <>
                        {/* İlk madde normal div */}
                        <div className="leading-relaxed text-[15px] block">
                          • {articlesItems[0].title}{" "}
                          {articlesItems[0].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[0].note})
                            </span>
                          )}
                        </div>

                        {/* İkinci madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[1].title}{" "}
                          {articlesItems[1].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[1].note})
                            </span>
                          )}
                        </a>

                        {/* Üçüncü madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link2}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[2].title}{" "}
                          {articlesItems[2].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[2].note})
                            </span>
                          )}
                        </a>

                        {/* Dördüncü madde linkli */}
                        <a
                          className="leading-relaxed text-[15px] text-black hover:text-black visited:text-black block"
                          href={lawyer.link3}
                          target="_blank"
                          rel="noreferrer"
                        >
                          • {articlesItems[3].title}{" "}
                          {articlesItems[3].note && (
                            <span className="italic text-[14px]">
                              ({articlesItems[3].note})
                            </span>
                          )}
                        </a>
                      </>
                    ) : (
                      // 🔹 Diğer tüm durumlar
                      articlesItems.map((a, i) => (
                        <div key={i} className="leading-relaxed text-[15px]">
                          • {a.title}{" "}
                          {a.note && (
                            <span className="italic text-[14px]">
                              ({a.note})
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sağ Sütun */}
            <div className="w-full lg:w-1/2 space-y-10">
              <div>
                <h2 className="text-[20px] sm:text-[22px] font-bold">Eğitim</h2>
                <div className="mt-4 space-y-3">
                  {educationItems.length ? (
                    educationItems.map((e, i) => (
                      <div key={i} className="leading-relaxed text-[15px]">
                        • {e.school}
                        {e.year && (
                          <div className="text-slate-600 italic text-[14px]">
                            {e.year}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-500">-</div>
                  )}
                </div>
              </div>

              {languageItems.length > 0 && (
                <div>
                  <h2 className="text-[20px] sm:text-[22px] font-bold">Dil</h2>
                  <div className="mt-4 space-y-2">
                    {languageItems.length ? (
                      languageItems.map((e, i) => (
                        <div key={i} className="leading-relaxed text-[15px]">
                          • {e}
                        </div>
                      ))
                    ) : (
                      <div className="text-slate-500">-</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Özgeçmiş */}
          {lawyer?.description ? (
            <div className="w-full">
              <h2 className="text-[20px] sm:text-[22px] font-bold">Özgeçmiş</h2>
              <p className="mt-4 mb-12 leading-relaxed text-[15px] text-justify">
                {lawyer?.description}
              </p>
            </div>
          ) : (
            <div className="mt-10"> </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default LawyerDetail;
